import { useMemo, useState } from 'react';
import { App, Button, Card, Form, Input, Modal, Select, Table, Tag, Typography } from 'antd';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { activitiesApi } from '@/core/api/activitiesApi';
import { crmApi } from '@/core/api/crmApi';
import { leadsApi } from '@/core/api/leadsApi';
import { salesApi } from '@/core/api/salesApi';
import type { Activity, ActivityType, CreateActivityRequest } from '@/core/types/activity';
import { useAuthStore } from '@/core/auth/authStore';
import { PageHeader } from '@/shared/components/PageHeader';
import { formatDateTime } from '@/core/utils/date';
import { JalaliDateTimePicker } from '@/shared/components/JalaliDateTimePicker';

type RelationType = 'leadId' | 'contactId' | 'accountId' | 'opportunityId';
interface Values { subject: string; type: ActivityType; dueAtUtc?: Dayjs; relationType: RelationType; relatedId: string; description?: string }
interface RelatedOption { value: string; label: string }

const leadLabel = (lead: { firstName: string | null; lastName: string | null; companyName: string | null; mobile: string | null }): string => {
  const person = [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim();
  return lead.companyName || person || lead.mobile || 'سرنخ بدون نام';
};

export const ActivitiesPage = (): React.ReactElement => {
  const { message } = App.useApp();
  const me = useAuthStore(s => s.me);
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [relatedSearch, setRelatedSearch] = useState('');
  const [form] = Form.useForm<Values>();
  const relationType = Form.useWatch('relationType', form) ?? 'leadId';

  const query = useQuery({ queryKey: ['activities'], queryFn: () => activitiesApi.list({ pageSize: 100 }) });
  const leadsQuery = useQuery({ queryKey: ['activity-related', 'leads', relatedSearch], queryFn: () => leadsApi.list({ page: 1, pageSize: 100, search: relatedSearch || undefined, sortBy: 'createdAtUtc', sortDirection: 'desc' }), enabled: open && relationType === 'leadId' });
  const contactsQuery = useQuery({ queryKey: ['activity-related', 'contacts', relatedSearch], queryFn: () => crmApi.contacts({ page: 1, pageSize: 100, search: relatedSearch || undefined }), enabled: open && relationType === 'contactId' });
  const accountsQuery = useQuery({ queryKey: ['activity-related', 'accounts', relatedSearch], queryFn: () => crmApi.accounts({ page: 1, pageSize: 100, search: relatedSearch || undefined }), enabled: open && relationType === 'accountId' });
  const opportunitiesQuery = useQuery({ queryKey: ['activity-related', 'opportunities', relatedSearch], queryFn: () => salesApi.opportunities(1, 100, relatedSearch || undefined), enabled: open && relationType === 'opportunityId' });

  const relatedOptions = useMemo<RelatedOption[]>(() => {
    if (relationType === 'leadId') return (leadsQuery.data?.items ?? []).map(x => ({ value: x.id, label: leadLabel(x) }));
    if (relationType === 'contactId') return (contactsQuery.data?.items ?? []).map(x => {
      const name = [x.firstName, x.lastName].filter(Boolean).join(' ').trim();
      const primary = x.contactPoints.find(p => p.isPrimary)?.value ?? x.contactPoints[0]?.value;
      return { value: x.id, label: name || primary || 'مخاطب بدون نام' };
    });
    if (relationType === 'accountId') return (accountsQuery.data?.items ?? []).map(x => ({ value: x.id, label: x.name }));
    return (opportunitiesQuery.data?.items ?? []).map(x => ({ value: x.id, label: x.name }));
  }, [relationType, leadsQuery.data, contactsQuery.data, accountsQuery.data, opportunitiesQuery.data]);

  const relatedLoading = relationType === 'leadId' ? leadsQuery.isLoading
    : relationType === 'contactId' ? contactsQuery.isLoading
      : relationType === 'accountId' ? accountsQuery.isLoading
        : opportunitiesQuery.isLoading;

  const create = useMutation({
    mutationFn: (r: CreateActivityRequest) => activitiesApi.create(r),
    onSuccess: () => { message.success('فعالیت ثبت شد.'); setOpen(false); setRelatedSearch(''); form.resetFields(); void client.invalidateQueries({ queryKey: ['activities'] }); },
    onError: () => message.error('ثبت فعالیت ناموفق بود.'),
  });
  const complete = useMutation({
    mutationFn: (activity: Activity) => activitiesApi.complete(activity.id, activity.rowVersion),
    onSuccess: () => { message.success('فعالیت تکمیل شد.'); void client.invalidateQueries({ queryKey: ['activities'] }); },
    onError: () => message.error('تکمیل فعالیت ناموفق بود.'),
  });
  const submit = (v: Values): void => create.mutate({
    subject: v.subject, type: v.type, dueAtUtc: v.dueAtUtc?.toISOString(),
    ownerMembershipId: me?.membership.id || '', [v.relationType]: v.relatedId, description: v.description,
  });

  return <div className="page-stack">
    <PageHeader title="فعالیت‌ها" description="وظایف، تماس‌ها، جلسات و یادداشت‌های مرتبط با CRM" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>فعالیت جدید</Button>} />
    <Card variant="borderless"><Table<Activity> rowKey="id" loading={query.isLoading} dataSource={query.data?.items} pagination={false} scroll={{ x: 'max-content' }} columns={[
      { title: 'موضوع', dataIndex: 'subject', render: v => <Typography.Text strong>{v}</Typography.Text> },
      { title: 'نوع', dataIndex: 'type' },
      { title: 'وضعیت', dataIndex: 'status', render: v => <Tag color={v === 'completed' ? 'green' : 'blue'}>{v === 'completed' ? 'تکمیل‌شده' : 'باز'}</Tag> },
      { title: 'سررسید', dataIndex: 'dueAtUtc', render: v => formatDateTime(v) },
      { title: 'تاریخ ثبت', dataIndex: 'createdAtUtc', render: formatDateTime },
      { title: '', render: (_, item) => item.status === 'open' ? <Button size="small" icon={<CheckOutlined />} loading={complete.isPending} onClick={() => complete.mutate(item)}>تکمیل</Button> : null },
    ]} locale={{ emptyText: 'فعالیتی ثبت نشده است.' }} /></Card>

    <Modal open={open} title="ثبت فعالیت" onCancel={() => { setOpen(false); setRelatedSearch(''); form.resetFields(); }} onOk={() => form.submit()} confirmLoading={create.isPending} destroyOnHidden>
      <Form form={form} layout="vertical" initialValues={{ type: 'task', relationType: 'leadId' }} onFinish={submit}>
        <Form.Item name="subject" label="موضوع" rules={[{ required: true, message: 'موضوع فعالیت را وارد کنید.' }]}><Input /></Form.Item>
        <Form.Item name="type" label="نوع"><Select options={[{ value: 'task', label: 'وظیفه' }, { value: 'call', label: 'تماس' }, { value: 'meeting', label: 'جلسه' }, { value: 'email', label: 'ایمیل' }, { value: 'note', label: 'یادداشت' }]} /></Form.Item>
        <Form.Item name="dueAtUtc" label="سررسید"><JalaliDateTimePicker /></Form.Item>
        <Form.Item name="relationType" label="نوع موضوع مرتبط">
          <Select onChange={() => { form.setFieldValue('relatedId', undefined); setRelatedSearch(''); }} options={[{ value: 'leadId', label: 'سرنخ' }, { value: 'contactId', label: 'مخاطب' }, { value: 'accountId', label: 'سازمان' }, { value: 'opportunityId', label: 'فرصت فروش' }]} />
        </Form.Item>
        <Form.Item name="relatedId" label="موضوع مرتبط" rules={[{ required: true, message: 'موضوع مرتبط را انتخاب کنید.' }]}>
          <Select
            showSearch
            allowClear
            loading={relatedLoading}
            options={relatedOptions}
            filterOption={false}
            onSearch={setRelatedSearch}
            onClear={() => setRelatedSearch('')}
            placeholder={relatedLoading ? 'در حال دریافت گزینه‌ها…' : 'سرنخ، مخاطب، سازمان یا فرصت فروش را انتخاب کنید'}
            notFoundContent={relatedLoading ? 'در حال دریافت…' : 'مورد مرتبطی برای نمایش وجود ندارد'}
          />
        </Form.Item>
        <Form.Item name="description" label="توضیحات"><Input.TextArea rows={3} /></Form.Item>
      </Form>
    </Modal>
  </div>;
};
